import { useContext, useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import { v4 as uuid } from 'uuid'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { db, timestamp } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { Auth } from '../../StateManagment/Auth'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import { useFirestoreGeneralOrderBy } from '../../firebase/useFirestore'
import { absenceWindow } from '../../helpers/availabilityHelpers/findAbsence'
import Table from '../common/table/Table.jsx'
import TableCell from '../common/table/TableCell.jsx'
import ReadOnlyGuard from '../common/ReadOnlyGuard'

const REASON_OPTIONS = [
    { value: 'vakantie', label: 'Vakantie' },
    { value: 'ziek', label: 'Ziek' },
    { value: 'verlof', label: 'Verlof' },
    { value: 'overig', label: 'Overig' },
]

const REASON_LABEL = REASON_OPTIONS.reduce((acc, o) => {
    acc[o.value] = o.label
    return acc
}, {})

const dateToInputValue = (ts) => {
    if (!ts) return ''
    const d = ts.toDate()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

const inputValueToTimestamp = (value) => {
    if (!value) return null
    const [y, m, d] = value.split('-').map(Number)
    if (!y || !m || !d) return null
    return firebase.firestore.Timestamp.fromDate(new Date(y, m - 1, d))
}

const Absences = ({ item }) => {
    const [auth] = useContext(Auth)
    const [, setSaved] = useContext(SavedIcon)
    const [localRows, setLocalRows] = useState([])

    const absences = useFirestoreGeneralOrderBy(
        'PersonaAbsences',
        'PersonaID',
        item?.ID || '',
        'StartDate',
        'asc'
    )

    useEffect(() => {
        setLocalRows(Array.isArray(absences) ? absences : [])
    }, [absences])

    const updateLocal = (docid, updates) => {
        setLocalRows((prev) =>
            prev.map((row) => (row.docid === docid ? { ...row, ...updates } : row))
        )
    }

    const updateAbsence = async (docid, updates) => {
        if (!docid) return
        await db.collection('PersonaAbsences').doc(docid).update(updates)
        setSaved('flex')
    }

    const startDateHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const value = e.currentTarget.value
        const ts = inputValueToTimestamp(value)
        updateLocal(docid, { StartDate: ts })
        if (ts) updateAbsence(docid, { StartDate: ts })
    }

    const endDateHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const value = e.currentTarget.value
        const ts = inputValueToTimestamp(value)
        updateLocal(docid, { EndDate: ts })
        updateAbsence(docid, { EndDate: ts })
    }

    // Tijden zijn optioneel: leeg betekent hele dagen, zoals het altijd was.
    // Ze verfijnen alleen de randen van het tijdvak — over meer dagen blijft het
    // één aaneengesloten blok.
    // AI-CONTEXT: See docs/features/afwezigheid-met-tijden.md
    const startTimeHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const value = e.currentTarget.value
        updateLocal(docid, { StartTime: value })
        updateAbsence(docid, { StartTime: value })
    }

    const endTimeHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const value = e.currentTarget.value
        updateLocal(docid, { EndTime: value })
        updateAbsence(docid, { EndTime: value })
    }

    const reasonHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const value = e.currentTarget.value
        updateLocal(docid, { Reason: value })
        updateAbsence(docid, { Reason: value })
    }

    const noteHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const value = e.currentTarget.value
        updateLocal(docid, { Note: value })
    }

    const noteBlurHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const value = e.currentTarget.value
        updateAbsence(docid, { Note: value })
    }

    // Soft delete (ADR-008). Een afwezigheid is een afspraak die de begeleider
    // handmatig heeft vastgelegd; per ongeluk weggeklikt was hij onherroepelijk
    // weg. Nu verdwijnt de regel uit alle lijsten — de useFirestoreQuery-engine
    // filtert soft deletes centraal, en de nachtklus doet het met de hand —
    // en staat hij in de prullenbak, waar hij terug te zetten is.
    const deleteAbsence = async (e) => {
        const docid = e.currentTarget.dataset.docid
        if (!docid) return
        await db.collection('PersonaAbsences').doc(docid).update({
            Deleted: true,
            DeletedAt: timestamp,
            DeletedBy: auth?.ID || '',
        })
        setSaved('flex')
    }

    const addAbsence = async () => {
        if (!item?.ID) return

        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const startTs = firebase.firestore.Timestamp.fromDate(today)

        await db.collection('PersonaAbsences').add({
            ID: uuid(),
            CompagnyID: client,
            PersonaID: item.ID,
            StartDate: startTs,
            EndDate: startTs,
            StartTime: '',
            EndTime: '',
            Reason: 'overig',
            Note: '',
            Position: (localRows?.length || 0) + 1,
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        })

        setSaved('flex')
    }

    const columns = [
        { key: 'start', label: 'VANAF', searchable: false },
        { key: 'startTime', label: 'VANAF TIJD', searchable: false },
        { key: 'end', label: 'T/M', searchable: false },
        { key: 'endTime', label: 'TOT TIJD', searchable: false },
        { key: 'reason', label: 'REDEN' },
        { key: 'note', label: 'NOTITIE', searchable: false },
        { key: 'delete', label: 'VERWIJDEREN', searchable: false },
    ]

    return (
        <div className="table-container dashboard-container">
            <h2>Geplande afwezigheid</h2>

            <ReadOnlyGuard edit="personas.edit">
                {(canEdit) => (
                    <Table
                        columns={columns}
                        items={localRows}
                        getItemKey={(row) => row.docid}
                        renderRow={(row) => (
                            <>
                                <TableCell>
                                    <input
                                        type="date"
                                        data-docid={row.docid}
                                        value={dateToInputValue(row.StartDate)}
                                        onChange={startDateHandler}
                                    />
                                </TableCell>

                                <TableCell>
                                    <input
                                        type="time"
                                        data-docid={row.docid}
                                        value={row.StartTime || ''}
                                        onChange={startTimeHandler}
                                        aria-label="Begintijd"
                                    />
                                </TableCell>

                                <TableCell>
                                    <input
                                        type="date"
                                        data-docid={row.docid}
                                        value={dateToInputValue(row.EndDate)}
                                        onChange={endDateHandler}
                                        placeholder="Onbepaald"
                                    />
                                </TableCell>

                                <TableCell>
                                    <input
                                        type="time"
                                        data-docid={row.docid}
                                        value={row.EndTime || ''}
                                        onChange={endTimeHandler}
                                        aria-label="Eindtijd"
                                    />
                                    {/* Een eindtijd vóór de begintijd levert een
                                        tijdvak op dat niet bestaat. Die regel telt
                                        nergens mee, dus hij moet het zeggen in
                                        plaats van stil niets te doen. */}
                                    {row.StartDate && !absenceWindow(row) ? (
                                        <span className="absence-time-warning">
                                            De eindtijd ligt vóór de begintijd — deze
                                            afwezigheid telt nog niet mee.
                                        </span>
                                    ) : null}
                                </TableCell>

                                <TableCell>
                                    <select
                                        data-docid={row.docid}
                                        value={row.Reason || 'overig'}
                                        onChange={reasonHandler}
                                    >
                                        {REASON_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </TableCell>

                                <TableCell>
                                    <input
                                        type="text"
                                        data-docid={row.docid}
                                        value={row.Note || ''}
                                        onChange={noteHandler}
                                        onBlur={noteBlurHandler}
                                        placeholder="Optionele notitie"
                                    />
                                </TableCell>

                                <TableCell>
                                    {canEdit && (
                                        <DeleteOutlinedIcon
                                            className="table-delete-icon"
                                            data-docid={row.docid}
                                            onClick={deleteAbsence}
                                        />
                                    )}
                                </TableCell>
                            </>
                        )}
                        searchAccessor={(row) => REASON_LABEL[row?.Reason] || ''}
                        searchPlaceholder="Zoeken op reden"
                        emptyMessage="Nog geen afwezigheid gepland."
                        addRow={canEdit ? {
                            toolTipContent: 'Afwezigheid toevoegen',
                            onClick: addAbsence,
                        } : undefined}
                    />
                )}
            </ReadOnlyGuard>
        </div>
    )
}

export default Absences
